// ── Material Alignment Gate ──
// Asserts that the materials a job is about to be installed against are the
// SAME materials that were specified, ordered, and prepared. Specifically:
//
//     specHash === poHash === kitHash
//
// Thin assertion layer over existing primitives. Does NOT introduce new
// schemas, lifecycle, or canonical state.
//
// Reuses:
//   • boundJobSpec.snapshot.billOfMaterials  → specHash
//   • OrderDraft pushed PO ids + PurchaseOrder line items → poHash
//   • MaterialKit.items → kitHash
//   • computeBomHash(...) hashing semantics (sku:qty, sorted, djb2 variant)
//
// Behavior:
//   • Any of the three hashes missing → ok = false  ("not ready")
//   • Hashes differ                  → ok = false  ("out of sync")
//   • All three equal                → ok = true
//
// No logging here. No mutation. Callers route their own toasts/UI.

import type { Job } from "./types";
import type { MaterialKit } from "./materialKit";
import type { OrderDraft } from "./orderDraft";
import type { PurchaseOrder } from "./VendorPOContext";
import { collectBomItems } from "./orderDraft";
import { resolveBoundSpec } from "./jobSpec";

function normalizeSkuForAlignment(rawSku: string): string {
  const sku = String(rawSku ?? "").trim().toUpperCase();
  if (!sku) return "";
  // Legacy aliases observed in historical PO rows.
  if (sku === "SENTREL") return "WALL-SEN-STD";
  if (sku === "CR") return "OTH-AUTO-CURTAIN-ROD";
  return sku;
}

// Shared hashing — must match orderDraft.computeBomHash semantics so that
// a draft's sourceBomHash can be compared apples-to-apples with the spec
// and kit hashes. Order-independent over (sku, quantity).
function hashItems(items: Array<{ sku: string; quantity: number }>): string {
  // Normalize by SKU totals first so split rows in the spec (same SKU
  // repeated across multiple lines) hash the same as aggregated PO/kit rows.
  const totals = new Map<string, number>();
  for (const item of items) {
    const sku = normalizeSkuForAlignment(String(item.sku ?? ""));
    if (!sku) continue;
    totals.set(sku, (totals.get(sku) ?? 0) + Math.max(0, Number(item.quantity ?? 0)));
  }
  const normalizedItems = Array.from(totals.entries()).map(([sku, quantity]) => ({ sku, quantity }));
  const normalized = normalizedItems
    .map((i) => `${i.sku}:${i.quantity}`)
    .sort()
    .join("|");
  let h = 5381;
  for (let i = 0; i < normalized.length; i++) {
    h = ((h << 5) + h + normalized.charCodeAt(i)) | 0;
  }
  return `bom_${(h >>> 0).toString(16)}_${normalizedItems.length}`;
}

// ── Per-source hashers ────────────────────────────────────────────────────

/** Hash the bound JobSpec's BOM. Returns undefined if no spec is bound. */
export function specBomHash(job: Job): string | undefined {
  const items = collectBomItems(job);
  if (!job.boundJobSpec) {
    // Legacy job without a bound spec — alignment can't be enforced because
    // there is no canonical spec to compare against. Treat as missing.
    if (items.length === 0) return undefined;
    // Legacy materialsList exists but no spec binding — also missing for
    // alignment purposes; the gate will short-circuit "not ready".
    return undefined;
  }
  if (items.length === 0) return undefined;
  return hashItems(items);
}

/**
 * Hash the union of pushed POs for this job. Returns undefined when no PO
 * has been pushed yet (treat as NOT READY for install).
 *
 * Sources POs in this priority:
 *   1. Draft.pushedPoIds → look up matching PurchaseOrder records (most
 *      precise — restricts to the POs created from the canonical Push).
 *   2. Fallback: any PurchaseOrder records with matching jobId (legacy
 *      seed/data path that predates Push tracking).
 */
export function poBomHash(args: {
  jobId: string;
  draft?: OrderDraft;
  purchaseOrders: PurchaseOrder[];
}): string | undefined {
  const { jobId, draft, purchaseOrders } = args;

  let pos: PurchaseOrder[] = [];
  if (draft && draft.status === "pushed" && draft.pushedPoIds?.length) {
    const ids = new Set(draft.pushedPoIds);
    pos = purchaseOrders.filter((po) => ids.has(po.poId));
  } else {
    pos = purchaseOrders.filter((po) => po.jobId === jobId);
  }
  if (pos.length === 0) return undefined;

  // Aggregate line items across vendors. Quantities for the same SKU sum.
  const totals = new Map<string, number>();
  for (const po of pos) {
    for (const li of po.items) {
      totals.set(li.sku, (totals.get(li.sku) ?? 0) + li.quantity);
    }
  }
  const items = Array.from(totals.entries()).map(([sku, quantity]) => ({ sku, quantity }));
  if (items.length === 0) return undefined;
  return hashItems(items);
}

/** Hash the MaterialKit's required items. Returns undefined if no kit. */
export function kitBomHash(kit: MaterialKit | undefined): string | undefined {
  if (!kit) return undefined;
  if (kit.items.length === 0) return undefined;
  return hashItems(
    kit.items.map((i) => ({ sku: i.sku, quantity: i.requiredQuantity })),
  );
}

// ── Assertion ─────────────────────────────────────────────────────────────

export type AlignmentMissing = "spec" | "po" | "kit";

export interface MaterialAlignmentVerdict {
  ok: boolean;
  /** All blocking reasons. Plain language. */
  reasons: string[];
  /** Which hash sources were missing entirely (vs. just mismatched). */
  missing: AlignmentMissing[];
  hashes?: { specHash?: string; poHash?: string; kitHash?: string };
}

const TITLE_OUT_OF_SYNC = "Materials Out of Sync";

export interface AlignmentSources {
  kit: MaterialKit | undefined;
  orderDrafts: Record<string, OrderDraft>;
  purchaseOrders: PurchaseOrder[];
}

/**
 * SINGLE PREDICATE for material alignment. Pure. Composes the three hashes
 * and returns a verdict. Callers wire this into:
 *   • dispatch / assignment paths (after executionReady, before kit/pay)
 *   • install phase unlock (in addition to isKitGateCleared)
 *   • installer push-mode UI (block card replaces work surface)
 *   • canonical materials readiness (description-only fold)
 *
 * Do not duplicate this logic inline.
 */
export function assertMaterialAlignment(
  job: Job,
  sources: AlignmentSources,
): MaterialAlignmentVerdict {
  const draft = sources.orderDrafts[job.id];
  const specHash = specBomHash(job);
  const poHash = poBomHash({
    jobId: job.id,
    draft,
    purchaseOrders: sources.purchaseOrders,
  });
  const kitHash = kitBomHash(sources.kit);

  const missing: AlignmentMissing[] = [];
  const reasons: string[] = [];

  if (!specHash) {
    missing.push("spec");
    reasons.push("Spec BOM not available — bind a JobSpec to enforce alignment.");
  }
  if (!poHash) {
    missing.push("po");
    reasons.push("No pushed Purchase Order on file — order has not been placed.");
  }
  if (!kitHash) {
    missing.push("kit");
    reasons.push("Material Kit not generated — initialize the kit from the bound spec.");
  }

  if (missing.length > 0) {
    return {
      ok: false,
      reasons,
      missing,
      hashes: { specHash, poHash, kitHash },
    };
  }

  // All three present — compare.
  const allEqual = specHash === poHash && poHash === kitHash;
  if (allEqual) {
    return { ok: true, reasons: [], missing: [], hashes: { specHash, poHash, kitHash } };
  }

  const mismatchReasons: string[] = [];
  if (specHash !== poHash) {
    mismatchReasons.push("Order does not match current spec.");
  }
  if (poHash !== kitHash) {
    mismatchReasons.push("Material Kit does not match the placed order.");
  }
  if (specHash !== kitHash && specHash === poHash) {
    // Edge case: spec === po, but kit drifted from both.
    mismatchReasons.push("Material Kit does not match the current spec.");
  }

  return {
    ok: false,
    reasons: ["Materials out of sync (spec vs order vs kit).", ...mismatchReasons],
    missing: [],
    hashes: { specHash, poHash, kitHash },
  };
}

// ── Toast / UI helpers ────────────────────────────────────────────────────
// Mirrors the shape used by kitDispatchGuard so callers can drop these in
// without inventing copy on a per-site basis.

export interface AlignmentBlockCopy {
  title: string;
  description: string;
}

export function describeAlignmentBlock(verdict: MaterialAlignmentVerdict): AlignmentBlockCopy {
  if (verdict.ok) return { title: "", description: "" };
  if (verdict.missing.length > 0) {
    // Single most-actionable missing source determines the headline.
    const head =
      verdict.missing.includes("po") ? "Order not yet placed." :
      verdict.missing.includes("kit") ? "Material Kit not generated." :
      "Spec not bound.";
    return {
      title: `Materials not ready for install — ${head}`,
      description: verdict.reasons.join(" "),
    };
  }
  return {
    title: TITLE_OUT_OF_SYNC,
    description:
      "The materials specified, ordered, and prepared do not match. Do not begin install. " +
      verdict.reasons.slice(1).join(" "),
  };
}

/** Plain-language summary suitable for inline badges / readiness panels. */
export function summarizeAlignment(verdict: MaterialAlignmentVerdict): string {
  if (verdict.ok) return "Spec, order, and kit aligned.";
  if (verdict.missing.includes("po")) return "Awaiting pushed Purchase Order.";
  if (verdict.missing.includes("kit")) return "Material Kit not generated.";
  if (verdict.missing.includes("spec")) return "Spec BOM unavailable.";
  return "Materials out of sync (spec vs order vs kit).";
}

export const ALIGNMENT_BLOCK_TITLE = TITLE_OUT_OF_SYNC;
